import { defineBoot } from '#q-app';
import { Loading, Notify } from 'quasar';
import { t } from 'boot/i18n';
import { computed, ref } from 'vue';
import type { Router } from 'vue-router';
import type {
  StrapiEndpoint,
  StrapiProviders,
} from '../shared/composables/useStrapiApi';
import {
  useStrapiGet,
  setJwtToLocalStorage,
  removeJwtFromLocalStorage,
  getJwtFromLocalStorage,
  clearMemoizedGetCache,
  hasNoActiveEnrollment,
} from '../shared/composables/useStrapiApi';
import { useRepo } from 'pinia-orm';
import { User } from 'src/modules/User/models/User';
import { UserCourse } from 'src/modules/Course/models/UserCourse';
import { UserSection } from 'src/modules/Section/models/UserSection';
import { UserStep } from 'src/modules/Step/models/UserStep';
import { useGlobalStore } from 'src/stores/global';
import { readPendingJoin } from 'src/modules/School/composables/useStudentJoin';
import {
  decideAuthGuardRedirect,
  getSchoolDefaultRouteName,
  isSchoolUser,
  isSchoolPath,
  isChildWithoutValidLicense,
  isSchoolHost,
  normalizeRouteName,
} from './authGuards';

// A provider-login user created this recently was created by that login.
const NEW_ACCOUNT_WINDOW_MS = 2 * 60 * 1000;

const isAuthenticated = ref(false);
const userRepo = useRepo(User);
const _currentUser = ref<User | null>(null);

const userStepRepo = useRepo(UserStep);
const userSectionRepo = useRepo(UserSection);
const userCourseRepo = useRepo(UserCourse);

async function checkAuth() {
  if (!getJwtFromLocalStorage()) return;

  try {
    const params = {
      // firstname/lastname are common identity on the user now (name phase).
      fields: ['blocked', 'email', 'firstname', 'lastname'],
      populate: {
        profile: {
          populate: ['address', 'phone'],
        },
        // children live under the parent collection now (backend Phase 4); we
        // remap parent.children -> children below so consumers stay unchanged.
        parent: {
          populate: {
            children: {
              populate: {
                child_user: {
                  populate: {
                    role: {
                      fields: ['type'],
                    },
                  },
                  fields: ['blocked', 'firstname', 'lastname'],
                },
                license: {
                  fields: ['assigned_at', 'valid_until'],
                  sort: { valid_until: 'desc' },
                },
                public_school: {
                  fields: ['short_name', 'full_name'],
                  populate: { city: { fields: ['city'] } },
                },
              },
            },
          },
        },
        child: {
          fields: ['school', 'class', 'is_active'],
          populate: {
            license: {
              fields: ['assigned_at', 'valid_until'],
              sort: { valid_until: 'desc' },
            },
            public_school: {
              fields: ['short_name', 'full_name'],
              populate: { city: { fields: ['city'] } },
            },
          },
        },
        avatar: {
          fields: ['style', 'seed', 'avatar_config', 'avatar_svg'],
        },
        role: {
          fields: ['type'],
        },
        // school: {
        //   fields: ['documentId', 'full_name', 'short_name'],
        // },
      },
    };
    const url = 'users/me';
    const { execute, error, data } = useStrapiGet(url, params);

    await execute().catch((e: unknown) => {
      console.error('Auth check failed:', e);
      // Don't re-throw, just log
    });

    if (error.value || !data.value) {
      isAuthenticated.value = false;
      removeJwtFromLocalStorage();
      return;
    }

    // Backend Phase 4: children moved under parent. Name phase: firstname/lastname
    // are common identity on the user. Remap to the legacy shape so the User model's
    // relations and all consumers keep working:
    //  - children come from parent.children; their name is flattened from child_user
    //  - profile/own-child name is seeded from the user's firstname/lastname
    type ChildRaw = {
      child_user?: { firstname?: string; lastname?: string } | null;
    } & Record<string, unknown>;
    const userData = data.value as {
      firstname?: string;
      lastname?: string;
      parent?: { children?: ChildRaw[] };
      children?: ChildRaw[];
      profile?: Record<string, unknown> | null;
      child?: Record<string, unknown> | null;
    } & Record<string, unknown>;

    // A parent's children come from parent.children; each child's name is common
    // identity on its child_user, flattened onto the Child ORM object for display.
    userData.children = (userData.parent?.children ?? []).map((c) => ({
      ...c,
      firstname: c?.child_user?.firstname,
      lastname: c?.child_user?.lastname,
    }));
    delete userData.parent;

    _currentUser.value = userRepo.save(userData);

    isAuthenticated.value = !!_currentUser.value?.id;
    if (!isAuthenticated.value) removeJwtFromLocalStorage();
  } catch (e) {
    console.error('checkAuth error:', e);
    isAuthenticated.value = false;
    removeJwtFromLocalStorage();
  }
}

const currentUser = computed(() => {
  return _currentUser.value?.id
    ? userRepo.withAllRecursive().find(_currentUser.value?.id)
    : undefined;
});

let _router: Router | null = null;

/**
 * The generic "you need to sign in" bounce.
 *
 * Which login that is depends on the domain, not the route: the school domain is
 * sold as its own product, so a visitor there must never be dropped on the
 * parent/child login. Every unauthenticated bounce funnels through here, so this
 * one check covers `/` and every guarded route alike.
 */
function redirectToLogin() {
  if (!_router) return;
  const name = isSchoolHost(
    window.location.hostname,
    import.meta.env.VITE_SCHOOL_HOSTS as string | undefined,
  )
    ? 'school.login'
    : 'users.signin';
  void _router.replace({ name });
}

function redirectToSchoolLogin() {
  if (_router) void _router.replace({ name: 'school.login' });
}

function redirectToDashboard() {
  if (_router) void _router.push({ name: 'dashboard' });
}

function redirectToUserDefault() {
  if (!_router) return;
  if (isChildWithoutValidLicense(currentUser.value)) {
    void _router.push({ name: 'courses' });
    return;
  }
  void _router.push({ name: 'dashboard' });
}

function redirectToSchoolDefault() {
  if (!_router) return;
  void _router.push({ name: getSchoolDefaultRouteName(currentUser.value) });
}

/**
 * An invite link stashed before sign-in outranks the role default: the student
 * came here to join a class, so the join page is where they expect to land.
 * Returns true once the stash has been consumed and a redirect is under way.
 */
function redirectToPendingJoin(): boolean {
  // `readPendingJoin` clears the stash, so never call it without a router to
  // redirect with — the invite would be dropped silently.
  if (!_router) return false;
  if (currentUser.value?.role?.type !== 'student') return false;

  const pending = readPendingJoin();
  if (!pending) return false;

  void _router.push({
    name: 'school.class.join',
    query: {
      classDocumentId: pending.classDocumentId,
      ...(pending.semesterDocumentId
        ? { semesterDocumentId: pending.semesterDocumentId }
        : {}),
    },
  });
  return true;
}

async function signIn(token: string) {
  setJwtToLocalStorage(token);
  clearRepos();
  // The SPA is not reloaded between sessions, so drop the previous user's responses.
  clearMemoizedGetCache();
  // Fresh session — re-evaluate enrollment gating from scratch.
  hasNoActiveEnrollment.value = false;
  await checkAuth();
  if (!isAuthenticated.value) return;
  // Post-auth routing lives here, in the one place every login door funnels
  // through, so the destination follows the account's role rather than the form
  // it was typed into.
  if (redirectToPendingJoin()) return;
  if (isSchoolUser(currentUser.value)) {
    redirectToSchoolDefault();
    return;
  }
  redirectToUserDefault();
}

function clearRepos() {
  userStepRepo.withAllRecursive().flush();
  userSectionRepo.withAllRecursive().flush();
  userCourseRepo.withAllRecursive().flush();
  userRepo.flush();
}

function signOut() {
  const shouldRedirectToSchoolLogin =
    isSchoolUser(currentUser.value) ||
    isSchoolPath(_router?.currentRoute.value.path || '');

  isAuthenticated.value = false;
  removeJwtFromLocalStorage();
  // Clear all pinia stores
  clearRepos();
  clearMemoizedGetCache();
  hasNoActiveEnrollment.value = false;

  if (shouldRedirectToSchoolLogin) {
    redirectToSchoolLogin();
    return;
  }

  redirectToLogin();
}

// Export auth composable (unchanged logic)
export function useAuth() {
  return {
    signOut,
    signIn,
    isAuthenticated,
    currentUser,
  };
}

// "async" is optional;
// more info on params: https://v2.quasar.dev/quasar-cli/boot-files
export default defineBoot(async ({ router }) => {
  _router = router;

  // Show an in-app loading overlay during initial auth check (mobile-friendly)
  Loading.show({
    spinnerSize: 32,
    delay: 100,
  });
  try {
    await checkAuth();
  } finally {
    Loading.hide();
  }

  router.beforeEach(async (to) => {
    const {
      name,
      query: { id_token },
      meta: { requiresAuth = true },
    } = to;

    if (id_token) {
      await signIn(id_token as string);
    }

    const routeName = normalizeRouteName(name);
    // Check if localstorage JWT was cleared, get the value from there
    if (isAuthenticated.value) {
      const localJwt = getJwtFromLocalStorage(true);
      if (!localJwt) {
        isAuthenticated.value = false;
      }
    }

    const schoolRoles = Array.isArray(to.meta?.schoolRoles)
      ? (to.meta.schoolRoles as string[])
      : [];

    const guardDecision = decideAuthGuardRedirect({
      isAuthenticated: isAuthenticated.value,
      routeName,
      path: to.path,
      requiresAuth: Boolean(requiresAuth),
      currentUser: currentUser.value,
      schoolRoles,
    });

    if (!guardDecision.allow) {
      if (guardDecision.redirectTo === 'users.signin') {
        return redirectToLogin();
      }
      if (guardDecision.redirectTo === 'school.login') {
        return redirectToSchoolLogin();
      }
      if (guardDecision.redirectTo === 'dashboard') {
        return redirectToDashboard();
      }
      return void router.replace({ name: guardDecision.redirectTo });
    }

    if (name === 'users.connect') {
      const provider = to.params.provider as StrapiProviders;
      const providerUrl: StrapiEndpoint = `/auth/${provider}/callback`;
      // Strapi >= 5.56 redirects here with no tokens in the URL and finishes
      // OAuth from the session cookie set at /connect/:provider, so the
      // cross-site callback must send credentials; it answers with our JWT.
      // `?error=` means the provider step itself failed: nothing to finish.
      if (!to.query.error) {
        const { execute } = useStrapiGet(providerUrl);
        const { data } = await execute({ withCredentials: true });
        const { jwt, user } =
          (data as { jwt?: string; user?: { createdAt?: string } } | null) ??
          {};
        if (jwt) {
          await signIn(jwt);
          // Provider login silently creates a parent for an unknown email;
          // a fresh createdAt is the only sign this login made the account.
          const createdMs = Date.parse(user?.createdAt ?? '');
          if (Date.now() - createdMs < NEW_ACCOUNT_WINDOW_MS) {
            Notify.create({
              type: 'positive',
              message: t('ACCOUNT_CREATED_WELCOME'),
            });
          }
          return;
        }
      }
      // Failed (the error toast is already up): back to this brand's login,
      // not the SET App page this route renders.
      redirectToLogin();
    }
  });

  router.afterEach((to) => {
    // TODO: If you need to set page titles, use a Pinia store directly here
    const globalStore = useGlobalStore();
    globalStore.setTitle((to.meta?.headerTitle ?? '') as string);
  });
});
